import { Link } from "react-router-dom";
import type { CategoryScore, ExamAttempt } from "@/types";

interface ExamResultsProps {
  attempt: ExamAttempt;
  categoryScores?: CategoryScore[];
  passMark?: number;
  needsSubscription?: boolean;
}

export default function ExamResults({ attempt, categoryScores, passMark, needsSubscription }: ExamResultsProps) {
  const passed = attempt.passed;
  const score = attempt.score ?? 0;
  const total = attempt.total_questions;
  const percentage = Math.round((score / total) * 100);
  const timeTaken = attempt.time_taken_seconds ?? 0;
  const minutes = Math.floor(timeTaken / 60);
  const seconds = timeTaken % 60;

  return (
    <div className="page-enter">
      <div className="text-center mb-8">
        <div
          className={`inline-flex h-24 w-24 items-center justify-center rounded-full ${
            passed ? "bg-success-100" : "bg-danger-100"
          } mb-4`}
        >
          {passed ? (
            <svg className="h-12 w-12 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="h-12 w-12 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
        </div>

        <div
          className={`inline-block rounded-full px-6 py-2 text-lg font-bold ${
            passed ? "bg-success-100 text-success-800" : "bg-danger-100 text-danger-800"
          } mb-3`}
        >
          {passed ? "PASSED" : "FAILED"}
        </div>

        <h2 className="text-3xl font-bold text-gray-900">
          {score} / {total}
        </h2>
        <p className="text-lg text-gray-500 mt-1">{percentage}% correct</p>

        <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-500">
          <div className="flex items-center space-x-1">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              Time: {minutes}m {seconds}s
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Pass mark: {passMark ?? "?"}/{total}</span>
          </div>
        </div>
      </div>

      {categoryScores && categoryScores.length > 0 && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Breakdown</h3>
          <div className="space-y-3">
            {categoryScores.map((cat) => (
              <div key={cat.category_id} className="flex items-center justify-between">
                <span className="text-sm text-gray-700 flex-1">{cat.category_name}</span>
                <div className="flex items-center space-x-3 ml-4">
                  <div className="w-32 h-2 rounded-full bg-gray-200">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        cat.percentage >= 80
                          ? "bg-success-500"
                          : cat.percentage >= 60
                            ? "bg-warning-500"
                            : "bg-danger-500"
                      }`}
                      style={{ width: `${cat.percentage}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-600 w-16 text-right">
                    {cat.correct}/{cat.total}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {needsSubscription && (
        <div className="mb-6 rounded-xl border border-primary-200 bg-primary-50 p-6 text-center">
          <h3 className="text-lg font-semibold text-primary-900 mb-1">Your free trial is complete</h3>
          <p className="text-sm text-primary-700 mb-4">
            Get full access to all exams and practice questions to continue preparing for your test.
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors"
          >
            Get Full Access &rarr;
          </Link>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to={`/attempts/${attempt.id}/review`}
          className="inline-flex items-center justify-center rounded-lg bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 transition-colors"
        >
          Review Answers
        </Link>
        {!needsSubscription && (
          <Link
            to="/exams"
            className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Take Another Exam
          </Link>
        )}
        <Link
          to="/history"
          className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 shadow-sm border border-gray-300 hover:bg-gray-50 transition-colors"
        >
          View History
        </Link>
      </div>
    </div>
  );
}
